import React from "react"
import { useDispatch } from "react-redux"
import dayjs from "dayjs"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import Alert from "@mui/material/Alert"
import AlertTitle from "@mui/material/AlertTitle"
import { useQuery, useMutation } from "@apollo/client"
import gql from "graphql-tag"
import duration from "dayjs/plugin/duration"
import { getCancelPolicy } from "../../../utils/lessonCancel"
import { logError, logErrorMessage } from "../../../utils/errors"
import { setSnackbar } from "../../../actions/globalUiActions"
import Loading from "../../template-parts/Loading"
import { INITIATED_BYS, toGQLInitiatedBy } from "../../../config"
import {
  CancelLessonDocument,
  RemoveStudentFromLessonDocument,
} from "../../../actions/bookingActions.graphqlTypes"
import { GetLessonDocument } from "./LessonCancelForm.graphqlTypes"

dayjs.extend(duration)

gql`
  query GetLesson($instructorId: ID!, $lessonId: ID!) {
    lessonByInstructorId(instructorId: $instructorId, lessonId: $lessonId) {
      id
      startTime
      price
      instructor {
        id
        timeZone
      }
      studentDetailsList {
        studentId
      }
    }
  }
`

interface LessonCancelFormProps {
  instructorId: string
  lessonId: string
  onComplete: {
    (studentId: string): void
    (): void
  }
  studentIdToRemove?: string | null
}

/**
 * This can be called from the lesson level or the student level.
 * Depending on how it's called, we change what we show the caller,
 * and we cancel different parts of the stored data.
 */
const LessonCancelForm: React.FC<LessonCancelFormProps> = ({
  instructorId,
  lessonId,
  onComplete,
  studentIdToRemove = null,
}) => {
  const dispatch = useDispatch()
  const [reasonForCancel, setReasonForCancel] = React.useState<string>("")
  const [initiatedBy, setInitiatedBy] = React.useState<string>(INITIATED_BYS[2])

  const { loading, data } = useQuery(GetLessonDocument, {
    variables: {
      instructorId,
      lessonId,
    },
  })

  const handleSuccess = () => {
    const message = studentIdToRemove ? "Student removed." : "Lesson canceled."
    dispatch(setSnackbar(message, "success"))
    if (studentIdToRemove) {
      onComplete(studentIdToRemove)
    } else {
      onComplete()
    }
  }

  const handleError = (error: Error) => {
    logError(error)
    dispatch(setSnackbar("Failed to cancel lesson", "error"))
  }

  const [cancelLessonMutation] = useMutation(CancelLessonDocument, {
    onCompleted: handleSuccess,
    onError: handleError,
  })

  const [removeStudentMutation] = useMutation(RemoveStudentFromLessonDocument, {
    onCompleted: handleSuccess,
    onError: handleError,
  })

  const lesson = data?.lessonByInstructorId
  const numStudents = lesson?.studentDetailsList?.length || 0

  const cancelPolicy = lesson
    ? getCancelPolicy({
        startTime: lesson.startTime,
        price: lesson.price,
        initiatedBy: toGQLInitiatedBy[initiatedBy],
        timeZone: lesson.instructor.timeZone,
      })
    : null

  const handleSubmit = () => {
    if (!studentIdToRemove) {
      // Cancel entire lesson
      cancelLessonMutation({
        variables: {
          query: {
            id: lessonId,
            instructorId,
          },
          input: {
            description: reasonForCancel,
            initiatedBy: toGQLInitiatedBy[initiatedBy],
            cancelPolicy: cancelPolicy || undefined,
          },
        },
      })
    } else {
      // Remove single student
      removeStudentMutation({
        variables: {
          query: {
            id: lessonId,
            instructorId,
            studentId: studentIdToRemove,
          },
          input: {
            description: reasonForCancel,
            initiatedBy: toGQLInitiatedBy[initiatedBy],
            cancelPolicy: cancelPolicy || undefined,
          },
        },
      })
    }
  }

  if (loading || !lesson) return <Loading />

  if (studentIdToRemove && numStudents === 1) {
    logErrorMessage(
      "LessonCancelForm got a student ID and they are the only student on the lesson.",
    )
    // This error code is arbitrary but searchable in the codebase. :)
    return <>A data error occured. Error code: C7382.</>
  }

  const cancelFormFields = (
    <>
      <TextField
        label="Reason for Cancellation"
        onChange={(e) => setReasonForCancel(e.target.value)}
        value={reasonForCancel}
        name="reasonForCancel"
        margin="normal"
        fullWidth
      />
      <TextField
        select
        label="Who Initiated Cancellation?"
        onChange={(e) => setInitiatedBy(e.target.value)}
        value={initiatedBy}
        name="initiatedBy"
        fullWidth
        margin="normal"
        data-cy="initiated-by-select"
      >
        {INITIATED_BYS.map((ib) => (
          <MenuItem value={ib} key={ib} data-cy="initiated-by-option">
            {ib}
          </MenuItem>
        ))}
      </TextField>
    </>
  )

  if (numStudents === 1 || studentIdToRemove) {
    const changeFeeMessage =
      cancelPolicy && cancelPolicy.changeFee > 0
        ? `This cancellation is subject to a $${cancelPolicy.changeFee} change fee. If appropriate, please inform the customer of this charge and then charge them in Stripe.`
        : null

    return (
      <div>
        <p>
          Cancelling this student&apos;s lesson will refund the lesson cost as
          Coastline credit, but will not refund to their credit card. If they do
          not intend to reschedule, additionally refund their payment in Stripe.
        </p>
        {changeFeeMessage && (
          <p>
            <strong>{changeFeeMessage}</strong>
          </p>
        )}
        {cancelPolicy?.message && (
          <Alert severity="info">
            <AlertTitle>Message for student</AlertTitle>
            {cancelPolicy?.message}
          </Alert>
        )}
        {cancelFormFields}
        <br />
        <p>Are you sure you want to continue?</p>
        <Button
          variant="contained"
          color="primary"
          style={{ marginRight: 20 }}
          onClick={handleSubmit}
        >
          Confirm
        </Button>
      </div>
    )
  }

  // Else, we have more than one student.
  // Show the warning about cancelling the whole lesson.
  // Don't show cancellation fee information.
  return (
    <>
      <Alert severity="warning">
        <AlertTitle>More than one student impacted</AlertTitle>
        You are about to cancel this lesson for more than one student. Please
        double check that the entire lesson should be cancelled. If you are
        trying to cancel for just one student, close this dialog, choose Edit,
        and remove the one student from the lesson.
      </Alert>
      {cancelFormFields}
      <Button
        variant="contained"
        color="primary"
        style={{ marginRight: 20, marginTop: 20 }}
        onClick={handleSubmit}
      >
        Confirm
      </Button>
    </>
  )
}

export default LessonCancelForm
